"use client";

import { useState } from "react";
import { HistoricalLineChart } from "@/components/charts/historical-line-chart";
import { DailyDividendYieldChart } from "@/components/charts/daily-dividend-yield-chart";
import type { ChartRangeLabel } from "@/components/charts/range-selector";
import { projectYieldOnCost, YIELD_ON_COST_GROWTH_CAP } from "@/lib/dividend";
import { formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { PricePoint, YearValue } from "@/lib/data/types";

/** "20J" (weiteste Hochrechnungs-Option) hat in der Chart-Anzeige kein 1:1-Aequivalent
 * (`ChartRangeLabel` kennt nur bis "10J"/"Max") - "Max" ist die sinnvollste Entsprechung
 * fuer "zeig die volle Historie". */
function toChartRange(label: ProjectionRangeLabel): ChartRangeLabel {
  return label === "20J" ? "Max" : label;
}

/** Eigene, kleinere Range-Menge als der gemeinsame CHART_RANGES (1J/3J/5J/10J/Max) -
 * semantisch ein Hochrechnungs-Horizont, kein Historie-Filter, daher bewusst kein
 * geteilter Typ mit components/charts/range-selector.tsx. */
const PROJECTION_RANGES = [
  { label: "1J", years: 1 },
  { label: "3J", years: 3 },
  { label: "5J", years: 5 },
  { label: "10J", years: 10 },
  { label: "20J", years: 20 },
] as const;

type ProjectionRangeLabel = (typeof PROJECTION_RANGES)[number]["label"];
type ViewMode = "verlauf" | "hochrechnung";

/**
 * Yield-on-Cost-Hochrechnung mit auswählbarem Zeithorizont (Erik 2026-07-11,
 * Feedback-Runde 8 Punkt 3: "nicht nur 50 Jahre sehen, sondern auswählen können
 * zwischen 5/10/20/50 Jahre"). `projectYieldOnCost` ist bereits eine reine, billige
 * Funktion (`lib/dividend.ts`) - die Auswahl berechnet daher clientseitig neu statt
 * einen Server-Roundtrip zu brauchen.
 *
 * Zweiter Modus "Rendite-Verlauf" (Master-Prompt 4, backlog.md -> „Ergänzende
 * Master-Prompts"): EINE Komponente mit zwei Modi statt zwei getrennten Charts, wie im
 * Backlog-Eintrag vorgeschlagen - Verlauf zeigt echte historische Dividendenrendite,
 * Hochrechnung die Yield-on-Cost-Prognose.
 *
 * **[Punkt 57+58] (2026-08-11)**: bis dahin zeigte "Rendite-Verlauf" zusaetzlich eine an
 * die Historie anschliessende Hochrechnungs-Linie - Erik empfand das als Vermischung von
 * Ist und Prognose innerhalb eines Modus, der laut eigenem Namen nur Historie zeigen
 * sollte. Beide Modi jetzt strikt getrennt: `DailyDividendYieldChart` bekommt keine
 * Projektionsdaten mehr, `HistoricalLineChart` im Hochrechnung-Modus hat zusaetzlich
 * `disableZoom` (Klick-und-Zieh-Zoom auf eine reine Prognosekurve bot keinen Mehrwert,
 * Hover/Tooltip bleiben unveraendert).
 */
export function DividendProjection({
  name,
  currentYield,
  growthRate,
  growthWindowYears,
  startYear,
  priceHistory,
  dividendPerShare,
}: {
  name: string;
  currentYield: number;
  growthRate: number;
  growthWindowYears: number;
  startYear: number;
  priceHistory: PricePoint[];
  dividendPerShare: YearValue[];
}) {
  const [mode, setMode] = useState<ViewMode>("verlauf");
  const [range, setRange] = useState<ProjectionRangeLabel>("10J");
  const years = PROJECTION_RANGES.find((r) => r.label === range)!.years;
  const projection = projectYieldOnCost(currentYield, growthRate, years, startYear);
  const isCapped = growthRate > YIELD_ON_COST_GROWTH_CAP;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1">
          {(
            [
              ["verlauf", "Rendite-Verlauf"],
              ["hochrechnung", "Hochrechnung"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                mode === key ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1">
          {PROJECTION_RANGES.map((r) => (
            <button
              key={r.label}
              type="button"
              onClick={() => setRange(r.label)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                range === r.label
                  ? "bg-card text-ink shadow-sm"
                  : "text-muted-foreground hover:text-ink",
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <h3 className="text-sm font-medium text-ink-secondary">
        {mode === "verlauf"
          ? `Dividendenrendite im Verlauf, Hochrechnung über ${years} weitere Jahre`
          : `Rendite auf Einstiegspreis — Hochrechnung über ${years} Jahre`}
      </h3>
      {/* Kurze Annahmen-Notiz VOR dem Chart statt eines langen Absatzes danach
          (Feedback-Runde 4, Punkt 12). Kein eigener Disclaimer mehr hier (2026-07-29,
          Disclaimer-Häufigkeit reduziert) - die Aktienseite zeigt ihn zentral einmal
          unter dem Score, unabhängig vom aktiven Tab. */}
      <p className="text-xs text-muted-foreground">
        Annahme: Dividende wächst ausgehend vom heutigen Kaufpreis mit der historischen{" "}
        {growthWindowYears}-Jahres-Wachstumsrate von {formatPercent(growthRate)} p.a. weiter
        {isCapped ? ` (gedeckelt auf ${formatPercent(YIELD_ON_COST_GROWTH_CAP)} p.a.)` : ""}.
      </p>
      {mode === "verlauf" ? (
        <DailyDividendYieldChart
          priceHistory={priceHistory}
          dividendPerShare={dividendPerShare}
          name={name}
          range={toChartRange(range)}
        />
      ) : (
        <HistoricalLineChart
          data={projection}
          referenceValue={currentYield}
          referenceLabel="Aktuelle Dividendenrendite"
          format="percent"
          color="#3F7A5B"
          valueLabel="Persönliche Dividendenrendite"
          disableZoom
        />
      )}
    </div>
  );
}
